import axiosInstance from "./axios";
import { occurrenceRsvpBody } from "../utils/occurrenceParticipation";

export function toRelative(u) {
  if (!u) return null;
  try {
    const url = new URL(u, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch {
    return u;
  }
}

// Fetches a single event by id — e.g. for opening it directly (via
// EventFocusModal) from a notification, without needing to already be on
// the Calendar page/visible date range.
export async function fetchEvent(eventId, token) {
  const { data } = await axiosInstance.get(`/events/${eventId}/`, {
    headers: { Authorization: `Token ${token}` },
  });
  return data;
}

// creates an event
export async function createEvent(payload, token) {
  const { data } = await axiosInstance.post("/events/", payload, {
    headers: {
      Authorization: `Token ${token}`,
    },
  });
  return data;
}

// Fetches the occurrences (scheduled instances) visible to the current user
// that overlap [start, end). Occurrences are generated by the backend — a
// recurring Event contributes one per instance. The backend requires both
// bounds and caps the window at 200 days; it pages via `next`, which is
// followed so callers get the complete set for the range.
//
// Canceled occurrences are opt-in (`includeCanceled`): Calendar shows them,
// anything listing upcoming events must not.
export async function fetchOccurrences(token, { start, end, signal, includeCanceled = false }) {
  const headers = { Authorization: `Token ${token}` };
  let url = "/event-occurrences/";
  let isFirstRequest = true;
  const results = [];

  while (url) {
    const { data } = await axiosInstance.get(url, {
      headers,
      // `next` is a relative path+query with start/end/limit/offset baked in.
      params: isFirstRequest
        ? { start, end, ...(includeCanceled ? { include_canceled: 1 } : {}) }
        : undefined,
      signal,
    });
    results.push(...(data.results || []));
    url = data.next || null;
    isFirstRequest = false;
  }

  return results;
}

// Fetches one occurrence by its occurrence id (NOT an event id) — used to
// resolve `event_occurrence` notification targets. 404s when the occurrence
// isn't visible or no longer exists (e.g. regenerated after a schedule edit).
// A canceled occurrence is returned, with status "canceled".
//
// EventOccurrenceDetailSerializer: the viewer's participation fields and
// `rsvps`, this occurrence's guest list as the backend allows it. Works for a
// guest invited to this occurrence only, who can't load the Event.
export async function fetchOccurrence(occurrenceId, token) {
  const { data } = await axiosInstance.get(`/event-occurrences/${occurrenceId}/`, {
    headers: { Authorization: `Token ${token}` },
  });
  return data;
}

// Updates an existing event. Only the event creator can succeed here —
// the backend enforces that (EventDetailView.perform_update).
//
// Edits are whole-series. For a recurring event, include `occurrence_id`
// (the occurrence the edit was opened from) and only send starts_at/ends_at
// when they actually changed: the backend keeps the series' anchor date and
// applies just the edited wall-clock time. Occurrences are regenerated after
// a schedule change, so previously held occurrence ids may stop existing.
//
// A series edit that would remove future occurrences is answered with a 409
// carrying a signed confirmation token (see occurrenceDeletionConfirmation).
// Pass `confirmationToken` to resend the exact same payload as confirmed.
export async function updateEvent(eventId, payload, token, confirmationToken = null) {
  const body = confirmationToken
    ? { ...payload, occurrence_confirmation_token: confirmationToken }
    : payload;
  const { data } = await axiosInstance.patch(`/events/${eventId}/`, body, {
    headers: { Authorization: `Token ${token}` },
  });
  return data;
}

// If `err` is the edit endpoint's "this will delete future occurrences"
// response, returns { token, count, impact }; otherwise null. Other 409s (e.g.
// occurrence_already_started) are ordinary errors and return null.
export function occurrenceDeletionConfirmation(err) {
  const res = err?.response;
  const data = res?.data;
  if (
    res?.status !== 409 ||
    data?.code !== "occurrence_deletion_confirmation_required" ||
    typeof data?.confirmation_token !== "string" ||
    !data.confirmation_token
  ) {
    return null;
  }
  const impact = data.impact || {};
  return {
    token: data.confirmation_token,
    count: Number(impact.occurrences_to_delete) || 0,
    impact: {
      occurrencesToDelete: Number(impact.occurrences_to_delete) || 0,
      customizedPreserved: Number(impact.customized_occurrences_preserved) || 0,
      scheduleOverridesRetained: Number(impact.schedule_overrides_retained) || 0,
      occurrencesToBackfill: Number(impact.occurrences_to_backfill) || 0,
    },
  };
}

// "Edit this occurrence" (creator only). `patch` holds only the changed
// fields among title, description, location, is_private, is_all_day,
// starts_at, ends_at; a content field set to null inherits the series value
// again. Returns the occurrence (same id and slot_date). Rejections carry
// { code, detail } — see eventErrorMessage.
export async function updateOccurrence(occurrenceId, patch, token) {
  const { data } = await axiosInstance.patch(`/event-occurrences/${occurrenceId}/`, patch, {
    headers: { Authorization: `Token ${token}` },
  });
  return data;
}

// Cancels one occurrence (creator only). Returns it with status "canceled"
// and its effective values frozen into overrides. Repeating it is a no-op.
export async function cancelOccurrence(occurrenceId, token) {
  const { data } = await axiosInstance.post(
    `/event-occurrences/${occurrenceId}/cancel/`,
    {},
    { headers: { Authorization: `Token ${token}` } }
  );
  return data;
}

// Deletes an event. Only the event creator can succeed here — the backend
// enforces that (EventDetailView.perform_destroy), same rule as updateEvent.
//   scope "series" (default): the whole Event and every occurrence → null.
//   scope "future": every occurrence that hasn't started; the series ends →
//     { event, deleted_count } (0 is a successful no-op).
export async function deleteEvent(eventId, token, { scope = "series" } = {}) {
  const { data } = await axiosInstance.delete(`/events/${eventId}/`, {
    headers: { Authorization: `Token ${token}` },
    params: scope === "future" ? { scope } : undefined,
  });
  return scope === "future" ? data : null;
}

// Invites a user (by id) to an event. Only the event creator can invite.
export async function inviteToEvent(eventId, userId, token) {
  const { data } = await axiosInstance.post(
    `/events/${eventId}/invite/`,
    { user_id: userId },
    { headers: { Authorization: `Token ${token}` } }
  );
  return data;
}

// Updates the current user's RSVP status for an event. Responds with the
// RSVP's { status, reminder } — the same shape as EventSerializer.my_rsvp.
export async function submitRsvp(eventId, status, token) {
  const { data } = await axiosInstance.post(
    `/events/${eventId}/rsvp/`,
    { status },
    { headers: { Authorization: `Token ${token}` } }
  );
  return data;
}

// Updates the current user's reminder for an event (it lives on their RSVP,
// not the Event). Same endpoint/response as submitRsvp.
export async function updateReminder(eventId, reminder, token) {
  const { data } = await axiosInstance.post(
    `/events/${eventId}/rsvp/`,
    { reminder },
    { headers: { Authorization: `Token ${token}` } }
  );
  return data;
}

// Removes the current user's RSVP for an event they don't own — i.e. "leaves"
// a non-owned event rather than deleting it. The backend itself blocks the
// event creator from using this (EventRSVPView.delete raises a 400 for
// them), so this must never be called for an owned event.
export async function deleteEventRsvp(eventId, token) {
  await axiosInstance.delete(`/events/${eventId}/rsvp/`, {
    headers: { Authorization: `Token ${token}` },
  });
}

// Fetches the current user's pending event invitations. EventInviteListView
// only ever returns unanswered ones (status "invited").
//
// `includeOccurrences` (?include_occurrences=1) adds invitations to single
// occurrences in the same list, by start; every item then has a `scope`
// ("series" / "occurrence") — see utils/eventInvites. Without it the list is
// series invitations only, as before.
//
// Paginated like /events/ — follows `next` (which keeps the query) so callers
// get the complete set rather than just the first page.
export async function fetchEventInvites(token, { includeOccurrences = false } = {}) {
  const headers = { Authorization: `Token ${token}` };
  let url = "/events/invites/";
  let isFirstRequest = true;
  const results = [];

  while (url) {
    const { data } = await axiosInstance.get(
      url,
      isFirstRequest && includeOccurrences ? { headers, params: { include_occurrences: 1 } } : { headers }
    );
    results.push(...(data.results || []));
    url = data.next || null;
    isFirstRequest = false;
  }

  return results;
}

// ---------------------------------------------------------------------------
// One occurrence's participation (never the series RSVP).
// ---------------------------------------------------------------------------

// POST /event-occurrences/{id}/rsvp/ — the viewer's RSVP for this occurrence
// only. `update` is { status?: "going" | "declined", reminder?: value | "none"
// | null }: an omitted field is unchanged, reminder null inherits the series
// reminder again (occurrenceRsvpBody). Responds with the participation after
// the change: { status, reminder, reminder_override, series_status,
// has_override }. Rejections: 409 occurrence_canceled, 404 not visible, 400.
export async function setOccurrenceRsvp(occurrenceId, update, token) {
  const { data } = await axiosInstance.post(
    `/event-occurrences/${occurrenceId}/rsvp/`,
    occurrenceRsvpBody(update),
    { headers: { Authorization: `Token ${token}` } }
  );
  return data;
}

// DELETE /event-occurrences/{id}/rsvp/ — clears the viewer's occurrence RSVP
// (status and reminder overrides). A series participant inherits the series
// RSVP again; an occurrence-only guest no longer takes part and loses access.
// 204, no body.
export async function clearOccurrenceRsvp(occurrenceId, token) {
  await axiosInstance.delete(`/event-occurrences/${occurrenceId}/rsvp/`, {
    headers: { Authorization: `Token ${token}` },
  });
}

// POST /event-occurrences/{id}/invite/ — invite a user to this occurrence
// only (creator only; 404 otherwise). Never grants the series. Responds
// { invited, status }: invited false = nothing changed (already pending, or
// already going here or through the series). A block either way: 403
// invite_not_allowed. A declined guest is re-invited (invited true).
export async function inviteToOccurrence(occurrenceId, userId, token) {
  const { data } = await axiosInstance.post(
    `/event-occurrences/${occurrenceId}/invite/`,
    { user_id: userId },
    { headers: { Authorization: `Token ${token}` } }
  );
  return data;
}
